import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHandPointUp } from '@fortawesome/pro-regular-svg-icons/faHandPointUp'
import { Button } from './EnhancedButton'

/**
 * Enhanced Buttons Renderer
 *
 * Renders a group of buttons with support for:
 * - Flexible layouts (row/column with spacing)
 * - Custom colors and styles
 * - Edit mode with selection highlighting
 * - Dynamic button configurations from the original Chakra implementation
 */

const Icon = () => (
    <div className="flex py-10 justify-center items-center w-full">
        <FontAwesomeIcon icon={faHandPointUp} size="6x" />
    </div>
)

const EnhancedButtonsRenderer = ({
    state: {
        buttons = [],
        direction = 'row',
        justifyContent = 'flex-start',
        spacing = 10,
        selectedButtonIndex = null,
    } = {},
    isEditMode = false,
    onChange = () => { },
    focused = false,
}) => {
    const inStorefront = !window?.location?.pathname.includes('/content/edit/')

    // Map variant names to Tailwind color classes
    const variantToColorMap = {
        primary: 'brand-primary',
        secondary: 'brand-secondary',
        tertiary_button: 'brand-tertiary',
        basket: 'brand-success',
        pdp_banner_red: 'brand-pdp-banner-red',
    }

    // Determine flex direction and alignment classes
    const directionClass = direction === 'row' ? 'flex-row' : 'flex-col'

    // Map justifyContent values to Tailwind classes
    const justifyMap = {
        'flex-start': 'justify-start',
        'flex-end': 'justify-end',
        center: 'justify-center',
        'space-between': 'justify-between',
        'space-around': 'justify-around',
    }
    const justifyClass = justifyMap[justifyContent] || 'justify-start'

    // Align items based on direction
    const alignClass = direction === 'column' ? 'items-start' : 'items-center'

    return (
        <>
            {buttons?.length > 0 ? (
                <div
                    className={`content__buttons flex ${directionClass} ${justifyClass} ${alignClass} gap-[${spacing}px]`}
                >
                    {buttons.map((button, idx) => {
                        // Generate margin classes for spacing
                        const marginClass = (() => {
                            if (idx === 0) return ''
                            if (
                                direction === 'row' &&
                                !['space-between', 'space-around'].includes(justifyContent)
                            ) {
                                return `ml-[${spacing}px]`
                            }
                            if (
                                direction === 'column' &&
                                !['space-between', 'space-around'].includes(justifyContent)
                            ) {
                                return `mt-[${spacing}px]`
                            }
                            return ''
                        })()

                        // Map variant to Tailwind color class
                        const buttonColor = variantToColorMap[button.variant] || 'brand-primary'

                        // Determine button style (solid, outline, ghost, link)
                        const buttonStyle = button.variantStyle || 'solid'

                        // Determine button size
                        const buttonSize = button.size || 'md'

                        // Build focus ring style for edit mode
                        const focusStyle =
                            selectedButtonIndex === idx && focused ? 'ring-4 ring-yellow-400' : ''

                        // Determine width class
                        const widthClass = button.width === 'full' ? 'w-full' : 'w-auto'

                        // Buttons are uppercase by default; opt out per-button
                        const caseClass = button.lowercaseText ? 'normal-case' : ''

                        return (
                            <div
                                key={`btn_${idx}_${Math.floor(Math.random() * 10000)}`}
                                className={marginClass}
                            >
                                <Button
                                    className={`content__button ${focusStyle} ${widthClass} ${caseClass}`}
                                    variantColor={buttonColor}
                                    variantStyle={buttonStyle}
                                    size={buttonSize}
                                    href={button.url}
                                    openInNewTab={button.openInNewTab}
                                    onClick={(e) => {
                                        if (isEditMode) {
                                            e.preventDefault()
                                            onChange({ selectedButtonIndex: idx })
                                        }
                                    }}
                                >
                                    {button.label}
                                </Button>
                            </div>
                        )
                    })}
                </div>
            ) : inStorefront ? (
                <></>
            ) : (
                        <Icon />
                    )}
        </>
    )
}

export default EnhancedButtonsRenderer
